import { useCallback, useMemo } from 'react';
import { Contact } from '../components/address-book';
import { useAddressSelectionContext } from '../context/address-selection-indicator/address-selection';
import {
  decreaseAddressSelectionCount,
  increaseAddressSelectionCount,
  resetAddresses,
  resetAddressSelectionCount,
  setAddressSelectionContacts,
  setAddressSelectionCount,
  setAddressSelectionIsMaxCountReached,
  setAddressSelectionMaxCount,
  setAddressSelectionSender,
  setAddressSelectionStatus,
} from '../context/address-selection-indicator/address-selection-actions';
import { initialAddressSelectionState } from '../context/address-selection-indicator/address-selection-state';
import { AddressSelectionStatus } from '../context/address-selection-indicator/address-selection-types';
import { useAnalyticsContext } from '../context/analytics-context/analytics-context';
import { EnvelopeType } from '../global-types/envelope';

type HandleSetSender = (params: { sender: Contact; enableRecipientAddresses: boolean }) => void;
type HandleSetContact = (contact: Contact) => void;
type HandleSetContacts = (contacts: Contact[]) => void;
type HandleUpdateContact = (contact: Contact, shouldIncrementRecipientPagination?: boolean) => Promise<void>;
type HandleDeleteContact = (contactId: string) => Promise<void>;
type HandleSelectionChange = () => void;
type HandleSetMaxCount = (maxCount: number) => void;
type HandleSetStatus = (status: boolean) => void;
type HandleResetCount = () => void;
type HandleResetAddresses = (params?: { envelope: EnvelopeType }) => void;

// placeholder function to handle a void action
const voidHandleSelectionChange: HandleSelectionChange = () => {
  return;
};

const useAddressSelection = () => {
  const { addressSelectionState, addressSelectionDispatch } = useAddressSelectionContext();
  const { sender, contacts, count, maxCount, status } = useMemo(() => addressSelectionState, [addressSelectionState]);
  const { isInitialAddress, isRecipientAddress, isMaxCountReached } = useMemo(() => status, [status]);
  const { trackUserAddManuallyRecipient } = useAnalyticsContext();

  // text to be displayed in the address-selection-indicator component
  const selection = useMemo(() => (maxCount > 0 ? count + '/' + maxCount : ''), [count, maxCount]);

  // handleSetSender sets the sender in the address-selection context
  const handleSetSender = useCallback<HandleSetSender>(
    ({ sender, enableRecipientAddresses }) => {
      setAddressSelectionSender(addressSelectionDispatch, sender);

      // when adding a sender address, the status should be updated
      // to indicate that the user is adding a recipient address now
      // and that the user already has addresses stored in the backend
      const newStatus: AddressSelectionStatus = {
        isInitialAddress: false,
        isRecipientAddress: enableRecipientAddresses,
        isMaxCountReached: false,
      };
      setAddressSelectionStatus(addressSelectionDispatch, newStatus);
    },
    [addressSelectionDispatch],
  );

  // handleSetRecipientContact adds the contact to the contacts array and increases the count
  const handleSetRecipientContact = useCallback<HandleSetContact>(
    (contact: Contact) => {
      const updatedContacts = [...contacts, contact];
      setAddressSelectionContacts(addressSelectionDispatch, updatedContacts);

      // increase the count when the user is adding a recipient address
      maxCount > 0 && maxCount !== count
        ? increaseAddressSelectionCount(addressSelectionDispatch)
        : voidHandleSelectionChange();
      trackUserAddManuallyRecipient();
    },
    [contacts, addressSelectionDispatch, maxCount, count, trackUserAddManuallyRecipient],
  );

  // handleSetRecipientContacts sets the contacts in the address-selection context
  // and updates the count to the length of the contacts array
  const handleSetRecipientContacts = useCallback<HandleSetContacts>(
    (contacts: Contact[]) => {
      setAddressSelectionContacts(addressSelectionDispatch, contacts);
    },
    [addressSelectionDispatch],
  );

  // handleUpdateRecipientContact updates the contact if the contact exists and the contact_id matches
  const handleUpdateRecipientContact = useCallback<HandleUpdateContact>(
    async (contact, shouldIncrementRecipientPagination = true) => {
      const updatedContacts = contacts.map((c: Contact) => (c.contact_id === contact.contact_id ? contact : c));
      setAddressSelectionContacts(addressSelectionDispatch, updatedContacts);

      // increase the count when the user is adding a recipient address
      shouldIncrementRecipientPagination && maxCount > 0 && maxCount !== count
        ? increaseAddressSelectionCount(addressSelectionDispatch)
        : voidHandleSelectionChange();
    },
    [addressSelectionDispatch, contacts, count, maxCount],
  );

  // handleDeleteRecipientContact deletes the contact from the contacts array and decreases the count
  // the count is decreased only when the count is greater than 1
  const handleDeleteRecipientContact = useCallback<HandleDeleteContact>(
    async (contactId: string) => {
      const updatedContacts = contacts.filter((contact) => contact.contact_id !== contactId);
      setAddressSelectionContacts(addressSelectionDispatch, updatedContacts);
      maxCount > 0 && count > 1 && decreaseAddressSelectionCount(addressSelectionDispatch);
    },
    [addressSelectionDispatch, contacts, count, maxCount],
  );

  // handleIncrease increases the counter by 1
  const handleIncrease = useCallback<HandleSelectionChange>(
    () =>
      maxCount > 0 && maxCount !== count
        ? increaseAddressSelectionCount(addressSelectionDispatch)
        : voidHandleSelectionChange(),
    [maxCount, count, addressSelectionDispatch],
  );

  // handleDecrease decreases the counter by 1
  const handleDecrease = useCallback<HandleSelectionChange>(
    () =>
      maxCount > 0 && count > 1 ? decreaseAddressSelectionCount(addressSelectionDispatch) : voidHandleSelectionChange(),
    [maxCount, count, addressSelectionDispatch],
  );

  // handleResetCount resets the count to 0 in cases where the user would not confirm the address selection
  // or any other scenario where the user would want to reset the count
  const handleResetCount = useCallback<HandleResetCount>(
    () => resetAddressSelectionCount(addressSelectionDispatch),
    [addressSelectionDispatch],
  );

  // handleResetAddresses resets the addresses to the initial state
  // when the user decreases the quantity we should keep the sender and status
  // and reset the contacts to an empty array (current behavior for the backend)
  // for return envelope it should reset everything but the sender
  // for blank envelope it should reset everything
  const handleResetAddresses = useCallback<HandleResetAddresses>(
    (
      params = {
        envelope: EnvelopeType.Blank,
      },
    ) => {
      const envelope = params.envelope;

      switch (envelope) {
        case EnvelopeType.ReturnRecipient:
          resetAddresses(addressSelectionDispatch, { ...initialAddressSelectionState, sender, status });
          break;
        case EnvelopeType.Return:
          resetAddresses(addressSelectionDispatch, { ...initialAddressSelectionState, sender });
          break;
        default:
          resetAddresses(addressSelectionDispatch, initialAddressSelectionState);
          break;
      }
    },
    [addressSelectionDispatch, sender, status],
  );

  // handleSetMaxCount sets the max count/items to be selected
  const handleSetMaxCount = useCallback<HandleSetMaxCount>(
    (maxCount) => {
      setAddressSelectionMaxCount(addressSelectionDispatch, maxCount);
    },
    [addressSelectionDispatch],
  );

  // handleSetCount sets the current count/recipient-addresses that have been added
  const handleSetCount = useCallback<HandleSetMaxCount>(
    (count) => {
      setAddressSelectionCount(addressSelectionDispatch, count);
    },
    [addressSelectionDispatch],
  );

  // handleSetInitialAddress sets when the user is adding the first address and haven't added any addresses before
  // if the backend returns any addresses, this status should be set to false
  const handleSetInitialAddress = useCallback<HandleSetStatus>(
    (newStatus: boolean) => {
      setAddressSelectionStatus(addressSelectionDispatch, { ...status, isInitialAddress: newStatus });
    },
    [status, addressSelectionDispatch],
  );

  const handleSetIsRecipientAddressStatus = useCallback<HandleSetStatus>(
    (newStatus: boolean) => {
      setAddressSelectionStatus(addressSelectionDispatch, { ...status, isRecipientAddress: newStatus });
    },
    [status, addressSelectionDispatch],
  );

  // handleSetMaxCountReached determines if the max count has been reached to disable the checkbox card
  const handleSetMaxCountReached = useCallback<HandleSelectionChange>(() => {
    if (maxCount === 0) return;
    if (count !== maxCount && !isMaxCountReached) return;
    setAddressSelectionIsMaxCountReached(addressSelectionDispatch, count === maxCount);
  }, [count, maxCount, isMaxCountReached, addressSelectionDispatch]);

  return {
    sender,
    contacts,
    count,
    maxCount,
    isInitialAddress,
    isRecipientAddress,
    isMaxCountReached,
    selection,
    handleSetSender,
    handleSetRecipientContact,
    handleSetRecipientContacts,
    handleUpdateRecipientContact,
    handleDeleteRecipientContact,
    handleIncrease,
    handleDecrease,
    handleResetCount,
    handleResetAddresses,
    handleSetMaxCount,
    handleSetCount,
    handleSetInitialAddress,
    handleSetIsRecipientAddressStatus,
    handleSetMaxCountReached,
  };
};

export default useAddressSelection;
